import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/client/lib/api-client'

// ── Account-level types ──────────────────────────────────────

export interface BingAccount {
  id: string
  email: string | null
  site_count: number
  created_at: string
}

/** List all Bing accounts */
export function useBingAccounts() {
  return useQuery({
    queryKey: ['bing-accounts'],
    queryFn: () => apiClient.get<{ accounts: BingAccount[] }>('/api/sites/bing-accounts'),
    staleTime: 5 * 60 * 1000,
  })
}

/** Add a Bing account (verify key + store) */
export function useAddBingAccount() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: { apiKey: string; email?: string }) =>
      apiClient.post<{ id: string; siteCount: number }>('/api/sites/bing-accounts', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bing-accounts'] })
    },
  })
}

/** Remove a Bing account + linked connections */
export function useRemoveBingAccount() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      apiClient.delete<{ ok: boolean }>(`/api/sites/bing-accounts/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bing-accounts'] })
      queryClient.invalidateQueries({ queryKey: ['bing'] }) // refresh per-site bing status too
    },
  })
}

// ── Per-site types ──────────────────────────────────────────────

export interface BingConnection {
  id: string
  siteUrl: string
  verifiedAt: string | null
  createdAt: string
}

export interface BingQueryRow {
  query: string
  impressions: number
  clicks: number
  avgClickPosition: number
  avgImpressionPosition: number
}

export interface BingPageRow {
  url: string
  impressions: number
  clicks: number
  avgClickPosition: number
  avgImpressionPosition: number
}

export interface BingTrafficRow {
  date: string
  impressions: number
  clicks: number
}

/** Check Bing connection status for a site */
export function useBingStatus(siteId: string) {
  return useQuery({
    queryKey: ['bing', 'status', siteId],
    queryFn: () => apiClient.get<{
      connected: boolean
      connection: BingConnection | null
    }>(`/api/sites/${siteId}/bing/status`),
    enabled: !!siteId,
    staleTime: 5 * 60 * 1000,
  })
}

/** Connect Bing Webmaster Tools to a site */
export function useConnectBing(siteId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: { apiKey: string; siteUrl: string }) =>
      apiClient.post<{ ok: boolean; connection: BingConnection }>(
        `/api/sites/${siteId}/bing/connect`,
        data,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bing', 'status', siteId] })
      queryClient.invalidateQueries({ queryKey: ['bing', 'queries', siteId] })
    },
  })
}

/** Disconnect Bing from a site */
export function useDisconnectBing(siteId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => apiClient.delete<{ ok: boolean }>(`/api/sites/${siteId}/bing/disconnect`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bing'] })
    },
  })
}

/** Bing keyword query stats */
export function useBingQueries(siteId: string) {
  return useQuery({
    queryKey: ['bing', 'queries', siteId],
    queryFn: () => apiClient.get<{
      queries: BingQueryRow[]
    }>(`/api/sites/${siteId}/bing/queries`),
    enabled: !!siteId,
    staleTime: 30 * 60 * 1000, // 30 min (data cached 1hr server-side)
  })
}

/** Bing page stats */
export function useBingPages(siteId: string) {
  return useQuery({
    queryKey: ['bing', 'pages', siteId],
    queryFn: () => apiClient.get<{
      pages: BingPageRow[]
    }>(`/api/sites/${siteId}/bing/pages`),
    enabled: !!siteId,
    staleTime: 30 * 60 * 1000,
  })
}

/** Bing backlinks summary */
export function useBingBacklinks(siteId: string) {
  return useQuery({
    queryKey: ['bing', 'backlinks', siteId],
    queryFn: () => apiClient.get<{
      inboundLinks: number
      indexedPages: number
    }>(`/api/sites/${siteId}/bing/backlinks`),
    enabled: !!siteId,
    staleTime: 30 * 60 * 1000,
  })
}

/** Bing rank and traffic over time */
export function useBingTraffic(siteId: string) {
  return useQuery({
    queryKey: ['bing', 'traffic', siteId],
    queryFn: () => apiClient.get<{
      traffic: BingTrafficRow[]
    }>(`/api/sites/${siteId}/bing/traffic`),
    enabled: !!siteId,
    staleTime: 30 * 60 * 1000,
  })
}
